import {
  Badge,
  DotsLoader,
  Icon,
  IconSizeClasses,
  Tooltip,
} from '@eppendorf/vnls-react-components';
import { GridV2 } from '@eppendorf/vnls-react-components-preview';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAccessControlGuard } from '$shared/custom-hooks/useAccessControlGuard';
import { useCurrentUserId } from '$shared/custom-hooks/useCurrentUserId';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';

import { useAdditionalFeatureIsEnabled } from '$components/additional-feature-guard/additional-feature-is-enabled';
import { useFeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import {
  isSampleSafetyPlusPlan,
  useGetSubscription,
} from '$features/subscription/subscription-api';
import { useGetUser } from '$features/users/users.api';

import {
  DashboardSettingsButton,
  DashboardSettingsDialog,
  DeviceOverviewCard,
  LabHealthComparisonCard,
  RecentDeviationsCard,
  AlertsIncidentsCard,
  LatestAuditTrailCard,
  PendingInvitationsCard,
  LatestReportsCard,
  QuickActionsBar,
} from './components';
import { dashboardAvailability } from './dashboard-availability';
import type { DashboardTileId } from './dashboard.types';
import { useDashboardPreferences } from './use-dashboard-preferences';

import './dashboard.scss';

function getGreetingKey(
  hour: number,
):
  | 'dashboard.greeting.morning'
  | 'dashboard.greeting.afternoon'
  | 'dashboard.greeting.evening' {
  if (hour < 12) return 'dashboard.greeting.morning';
  if (hour < 18) return 'dashboard.greeting.afternoon';
  return 'dashboard.greeting.evening';
}

export function Dashboard() {
  const { t } = useTranslation();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const { data: subscription } = useGetSubscription();
  const { email, tenantId } = useTokenAttributes(['email', 'tenantId']);
  const currentUserId = useCurrentUserId(email, tenantId);
  const { data: currentUser } = useGetUser(currentUserId || '');
  const { preferences, isReady, savePreferences } =
    useDashboardPreferences(currentUserId);
  const hasReportHubAccess = useAdditionalFeatureIsEnabled('REPORT_HUB_ACCESS');
  const showRecentDeviations = useFeatureIsEnabled(
    'ESEVNLS_4552_DEVICE_EVENT_CATEGORIES',
  );
  const canInviteUsers = useAccessControlGuard('INVITE_USER');
  const canViewProfiles = useAccessControlGuard('VIEW_OTHERS_PROFILE_DETAILS');
  const canViewAuditTrail = useAccessControlGuard('VIEW_AUDIT_TRAIL_OVERVIEW');
  const canCreateNotificationRule = useAccessControlGuard('CREATE_NOTIFICATION_RULE');
  const hasSampleSafetyPlus = isSampleSafetyPlusPlan(subscription);
  const canViewDevices = useAccessControlGuard('VIEW_DEVICE_OVERVIEW');
  const canViewDeviceDetails = useAccessControlGuard('VIEW_DEVICE_DETAILS');
  const canViewIncidents = useAccessControlGuard('VIEW_OVERVIEW');
  const canReadStabilityReports = useAccessControlGuard('READ_STABILITY_REPORT');
  const canReadAuditTrailReports = useAccessControlGuard('READ_AUDIT_TRAIL_REPORT');
  const canRequestStabilityReport = useAccessControlGuard('REQUEST_STABILITY_REPORT');
  const canRequestAuditTrailReport = useAccessControlGuard('REQUEST_AUDIT_TRAIL_REPORT');
  const tiles = dashboardAvailability({
    canViewDevices,
    canViewDeviceDetails,
    canViewIncidents,
    canInviteUsers,
    canViewAuditTrail,
    canReadStabilityReports,
    canReadAuditTrailReports,
    hasSampleSafetyPlus,
    hasReportHubAccess,
    showRecentDeviations,
  });
  const eligibleTileIds = tiles.filter((tile) => tile.available).map((tile) => tile.id);
  const canCreateReport =
    hasSampleSafetyPlus &&
    hasReportHubAccess &&
    (canRequestStabilityReport || canRequestAuditTrailReport);
  const isTileVisible = (tileId: DashboardTileId) =>
    eligibleTileIds.includes(tileId) && !preferences.hiddenTileIds.includes(tileId);
  const showSummary = isTileVisible('labHealth');
  const showDataGrid = eligibleTileIds.some(
    (tileId) =>
      tileId !== 'labHealth' && tileId !== 'deviceOverview' && isTileVisible(tileId),
  );

  return (
    <>
      <div className="dashboard-page">
        <div className="dashboard__page-header">
          <div>
            <div className="dashboard__eyebrow">
              <span>{t('dashboard.title')}</span>
              <Tooltip
                trigger={
                  <Badge
                    color="purple"
                    size="small"
                    className="flex flex__ai--center gap-xs bg-purple-500 color-white font-weight-bold"
                  >
                    <Icon
                      name="flag"
                      size={IconSizeClasses.XXSmall}
                      className="bg-white"
                    />
                    {t('dashboard.beta')}
                  </Badge>
                }
              >
                {t('dashboard.betaTooltip')}
              </Tooltip>
            </div>
            <h1 className="dashboard__greeting title">
              {t(getGreetingKey(new Date().getHours()), {
                firstName: currentUser?.firstName ? `, ${currentUser.firstName}` : '',
              })}
            </h1>
          </div>
          {isReady && <DashboardSettingsButton onClick={() => setIsSettingsOpen(true)} />}
        </div>

        {!isReady ? (
          <div className="dashboard__loading">
            <DotsLoader />
          </div>
        ) : (
          <div className="dashboard">
            {showSummary && (
              <div className="dashboard__summary">
                {isTileVisible('labHealth') && <LabHealthComparisonCard />}
              </div>
            )}

            {isTileVisible('deviceOverview') && (
              <DeviceOverviewCard
                selectedDevices={preferences.selectedDevices}
                onConfigure={() => setIsSettingsOpen(true)}
              />
            )}

            {showDataGrid && (
              <GridV2 columns={2} gap="lg" className="dashboard__grid">
                {isTileVisible('recentDeviations') && <RecentDeviationsCard />}
                {isTileVisible('incidents') && <AlertsIncidentsCard />}
                {isTileVisible('auditTrail') && <LatestAuditTrailCard />}
                {isTileVisible('pendingInvitations') && (
                  <PendingInvitationsCard canViewProfiles={canViewProfiles} />
                )}
                {isTileVisible('reports') && (
                  <LatestReportsCard
                    canReadStabilityReports={canReadStabilityReports}
                    canReadAuditTrailReports={canReadAuditTrailReports}
                  />
                )}
              </GridV2>
            )}

            <QuickActionsBar
              canCreateNotificationRule={canCreateNotificationRule}
              canCreateReport={canCreateReport}
            />
          </div>
        )}
      </div>

      {isReady && (
        <DashboardSettingsDialog
          isOpen={isSettingsOpen}
          onOpenChange={setIsSettingsOpen}
          tiles={tiles}
          preferences={preferences}
          onSave={savePreferences}
        />
      )}
    </>
  );
}
